import React, { useEffect, useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useCommentAnnotations, useCommentModeState, useVeltClient, VeltCommentPin } from '@veltdev/react';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const scores = [6, 5, 5, 5, 3, 4, 6, 4, 5];
const scores2 = [1, 3, 2, 2, 4, 4, 5, 3, 2];
const labels = [100, 200, 300, 400, 500, 600, 700];

const options: any = {
  fill: true,
  responsive: true,
  animations: true,
  scales: {
    y: {
      min: 0,
    },
  },
  plugins: {
    legend: {
      display: true,
    },
  },
};

const LineChartWithComments = () => {
  const chartId = 'lineChartWithComments';
  const chartRef = useRef(null);
  const { client } = useVeltClient();
  const commentModeState = useCommentModeState();
  const commentAnnotations = useCommentAnnotations();
  const [chartCommentAnnotations, setChartCommentAnnotations] = React.useState([]);

  useEffect(() => {
    const chartCommentAnnotations = commentAnnotations?.filter((comment) => comment.context?.chartId === chartId);
    setChartCommentAnnotations(chartCommentAnnotations as any || []);
  }, [commentAnnotations]);

  const handleChartClick = (event: any) => {
    const chart: any = chartRef.current;
    if (chart) {
      const elements = chart.getElementsAtEventForMode(event.nativeEvent, 'nearest', { intersect: false }, true);
      if (elements.length > 0) {
        const element = elements[0];
        const datasetIndex = element.datasetIndex;
        const index = element.index;
        const dataset = chart.data.datasets[datasetIndex];
        const xValue = chart.data.labels[index];
        const yValue = dataset.data[index];
        const context = { seriesId: dataset.label, xValue, yValue, chartId };
        addManualComment(context);
      }
    }
  };

  const addManualComment = (context: any) => {
    try {
      if (client && commentModeState) {
        const commentElement = client.getCommentElement();
        commentElement.addManualComment({ context });
        commentElement.disableCommentMode();
      }
    } catch (error) {
      console.error('Error adding manual comment', error);
    }
  };

  const findPoint = (seriesId: any, xValue: any, yValue: any) => {
    const chart: any = chartRef.current;
    if (chart) {
      const dataset = chart.data.datasets.find((dataset: any) => dataset.label === seriesId);
      const index = chart.data.labels.indexOf(xValue);
      if (dataset && index !== -1) {
        const yValueInDataset = dataset.data[index];
        if (yValueInDataset === yValue) {
          return { x: chart.scales.x.getPixelForValue(index), y: chart.scales.y.getPixelForValue(yValue) };
        }
      }
    }
    return null;
  };

  const showCommentPin = (commentAnnotation: any) => {
    const context = commentAnnotation.context || {};
    const point = findPoint(context.seriesId, context.xValue, context.yValue);
    if (point) {
      const { x, y } = point;
      return (
        <div
          key={commentAnnotation.annotationId}
          style={{
            left: `${x}px`,
            top: `${y}px`,
            position: 'absolute',
            transform: 'translate(0%, -100%)',
            zIndex: 1000,
          }}
        >
          <VeltCommentPin annotationId={commentAnnotation.annotationId} />
        </div>
      );
    }
    return null;
  };

  const data = useMemo(() => {
    return {
      datasets: [
        {
          label: 'Mis datos',
          data: scores,
          tension: 0.3,
          borderColor: 'rgb(75, 192, 192)',
          pointRadius: 6,
          pointBackgroundColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.3)',
        },
        {
          label: 'Mis datos (2)',
          tension: 0.3,
          data: scores2,
          borderColor: 'green',
          backgroundColor: 'rgba(0, 255, 0, 0.3)',
          pointRadius: 6,
        },
      ],
      labels,
    };
  }, []);

  return (
    <div className="dashlet-card">
      <div className="dashlet-card-header">Line Chart With Comments</div>
      <div style={{ position: 'relative' }} onClick={handleChartClick} data-velt-manual-comment-container="true">
        <Line data={data} options={options} ref={chartRef} />
        {chartCommentAnnotations.map((comment, index) => showCommentPin(comment))}
      </div>
    </div>
  );
};

export default LineChartWithComments;
